/* eslint-disable no-console */
import { VIDEO_EVENTS } from '@bedrock/media-engine';
import { RECOVERY_ACTIONS, RECOVERY_TICK_MS } from './recovery.constants';

const { ENDED, ERROR, LOAD_START, LOADED_METADATA, PAUSE, PLAYING, SEEKING, TIME_UPDATE, WAITING } = VIDEO_EVENTS;

/**
 * Watches the engine events and escalates through the policy steps while the playback does not progress
 *
 * @param {Object} engine engine to recover, the step actions are its methods
 * @param {Object} policy see recovery.constants.js
 * @param {Function} dispatcher receives the engine events untouched
 * @returns {{observe: Function, stop: Function}} `observe` replaces the dispatcher given to the engine
 */
export const createRecoverySupervisor = (engine, policy, dispatcher) => {
  let timer = null;
  let since = 0;
  let step = 0;
  let isStarted = false;
  let isPaused = false;
  let isSeeking = false;
  let lastTime;

  const getPhases = () => policy[engine.isLive ? 'live' : 'vod'] || {};

  const getSteps = () => {
    const phases = getPhases();

    if (isSeeking) {
      return phases.seek;
    }

    return (isStarted ? phases.stall : phases.startup) || [];
  };

  const restartClock = () => {
    since = Date.now();
    step = 0;
  };

  const stop = () => {
    clearInterval(timer);
    timer = null;
  };

  const runAction = action => {
    console.log(`[Recovery] No progress for ${Date.now() - since}ms, running ${action}`);

    if (action === RECOVERY_ACTIONS.FAIL) {
      dispatcher(ERROR, { error: { message: 'Playback stalled', fatal: true } });

      return;
    }

    Promise.resolve(engine[action]()).catch(error => console.warn(`[Recovery] ${action} failed:`, error));
  };

  const tick = () => {
    const steps = getSteps();
    const elapsed = Date.now() - since;

    while (step < steps.length && elapsed >= steps[step].at) {
      steps[step].actions.forEach(runAction);
      step += 1;
    }

    if (step >= steps.length) {
      stop();
    }
  };

  const start = () => {
    if (!timer) {
      restartClock();
      timer = setInterval(tick, RECOVERY_TICK_MS);
    }
  };

  const observe = (event, payload) => {
    switch (event) {
      case LOAD_START:
        isStarted = false;
        isPaused = false;
        isSeeking = false;
        lastTime = undefined;
        stop();
        start();
        break;
      case LOADED_METADATA:
        // NOTE: a stream refreshing its metadata is still alive, even if not started yet
        if (!isStarted) {
          restartClock();
        }
        break;
      case PLAYING:
        isPaused = false;
        start();
        break;
      case WAITING: {
        const isBuffering = Boolean(payload && payload.isBuffering);

        if (policy.ignoreBuffering && isBuffering && !isSeeking) {
          stop();
        } else if (policy.ignoreBuffering || isBuffering) {
          if (!isPaused) {
            start();
          }
        } else {
          restartClock();
        }
        break;
      }
      case TIME_UPDATE: {
        const currentTime = payload && payload.currentTime;

        if (currentTime !== lastTime) {
          lastTime = currentTime;
          isStarted = true;
          isSeeking = false;
          restartClock();
        }
        break;
      }
      case SEEKING:
        // NOTE: only a seek of a started playback is guarded, the start position alignment is a part of the startup
        if (isStarted && !isPaused && getPhases().seek) {
          isSeeking = true;
          restartClock();
          start();
        }
        break;
      case PAUSE:
        isPaused = true;
        isSeeking = false;
        stop();
        break;
      case ENDED:
      case ERROR:
        stop();
        break;
      default:
        break;
    }

    return dispatcher(event, payload);
  };

  return { observe, stop };
};
